import type { InputMode, RiserShape, Verdict } from '../core/types';
import { clientId } from './leaderboard';

const API = (import.meta.env.VITE_TELEMETRY_API as string | undefined) ?? '/api/telemetry';
const KEY_SESSION = 'solta.session.id';
const KEY_RUN_NO = 'solta.run.no';
const KEY_ASKED = 'solta.survey.asked';
const SPACING_MS = 2100;

export const MAX_BATCH = 64;
export const SURVEY_FROM_RUN = 2;

export interface ObjectRecord {
  readonly riser: number;
  readonly length_beats: number;
  readonly shape: RiserShape;
  readonly blind: boolean;
  readonly stab: boolean;
  readonly dips: number;
  readonly timing: Verdict;
  readonly verdict: Verdict;
  readonly dt_ms: number;
  readonly follow: number | null;
  readonly release_dist: number | null;
  readonly on_target: boolean | null;
  readonly t_ms: number;
}

export interface RunTag {
  readonly run_id: string;
  readonly client_id: string;
  readonly session_id: string;
  readonly run_no: number;
  readonly seed: string;
  readonly mode: InputMode;
}

const memory = new Map<string, string>();

function storage(kind: 'local' | 'session'): Storage | null {
  try {
    return kind === 'local' ? window.localStorage : window.sessionStorage;
  } catch {
    return null;
  }
}

function read(kind: 'local' | 'session', key: string): string | null {
  try {
    return storage(kind)?.getItem(key) ?? memory.get(`${kind}:${key}`) ?? null;
  } catch {
    return memory.get(`${kind}:${key}`) ?? null;
  }
}

function write(kind: 'local' | 'session', key: string, value: string): void {
  try {
    const disk = storage(kind);
    if (disk === null) memory.set(`${kind}:${key}`, value);
    else disk.setItem(key, value);
  } catch {
    memory.set(`${kind}:${key}`, value);
  }
}

function uuid(): string {
  return typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `solta-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;
}

function sessionId(): string {
  const known = read('session', KEY_SESSION);
  if (known !== null && known.length >= 8) return known;
  const fresh = uuid();
  write('session', KEY_SESSION, fresh);
  return fresh;
}

function bumpRunNo(): number {
  const previous = Number(read('local', KEY_RUN_NO) ?? 0);
  const next = (Number.isFinite(previous) && previous >= 0 ? Math.floor(previous) : 0) + 1;
  write('local', KEY_RUN_NO, String(next));
  return next;
}

let tag: RunTag | null = null;
let buffer: ObjectRecord[] = [];
const queue: string[] = [];
let lastSent = Number.NEGATIVE_INFINITY;

function post(body: string): void {
  void fetch(API, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body,
    keepalive: true,
  }).catch(() => undefined);
}

function drain(): void {
  const now = Date.now();
  if (now - lastSent < SPACING_MS) return;
  const body = queue.shift();
  if (body === undefined) return;
  lastSent = now;
  post(body);
}

function enqueue(body: string): void {
  queue.push(body);
  drain();
}

function flush(): void {
  if (tag === null || buffer.length === 0) return;
  enqueue(JSON.stringify({ kind: 'objects', ...tag, objects: buffer }));
  buffer = [];
}

export function beginRun(seed: string, mode: InputMode): RunTag {
  flush();
  drain();
  tag = {
    run_id: uuid(),
    client_id: clientId(),
    session_id: sessionId(),
    run_no: bumpRunNo(),
    seed,
    mode,
  };
  return tag;
}

export function recordObject(record: ObjectRecord): void {
  if (tag === null) return;
  buffer.push(record);
  if (buffer.length >= MAX_BATCH) flush();
  else drain();
}

export function endRun(): void {
  flush();
}

export function surveyDue(): boolean {
  return tag !== null && tag.run_no >= SURVEY_FROM_RUN && read('session', KEY_ASKED) === null;
}

export function markSurveyShown(): void {
  write('session', KEY_ASKED, '1');
}

export function answerSurvey(yes: boolean): void {
  if (tag === null) return;
  enqueue(JSON.stringify({ kind: 'survey', ...tag, answer: yes ? 'yes' : 'no' }));
}

function beacon(): void {
  flush();
  const canBeacon = typeof navigator.sendBeacon === 'function';
  for (const body of queue.splice(0)) {
    if (!canBeacon || !navigator.sendBeacon(API, new Blob([body], { type: 'application/json' }))) post(body);
  }
}

if (typeof addEventListener === 'function') addEventListener('pagehide', beacon);
